import {Injectable} from '@angular/core';
import * as utils from "../lib/utils";
import {ApiService} from "./api/api.service";
import {NotificationService} from "./notification.service";
import {catchError, tap} from "rxjs/operators";
import {HttpErrorHandler} from "./http-error-handler.service";
import {ConstantProperty} from "../_models/constant-property";
import {ComponentType} from "../_models/component-type";
import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from "@angular/common/http";
import {EventConfigColumn} from "../_typing/config/config-column";
import {IDMap} from '../_typing/generic-types';
import {saveAs} from "file-saver";
import {FileField} from "../_models/api/generic-constant";
import {getColumnId} from "../tables/custom-events-table/utils";
import {NOTIFICATION_DURATION_MS} from "../shared/globals";
import {DEFAULT_FILE_DOWNLOAD_OPTION} from "../_typing/download-filetype";
import {ConstantDateFilter} from "../tables/component-events-table/component-events-table.service";

interface ComponentDataCsvRequestParams {
    start: string;
    format: string;
    end: string;
    component_type_id: string;
    constant_properties_ids?: string[];
}

// TODO make this general download service, Make it DRY
@Injectable({
    providedIn: 'root'
})
export class DownloadService {

    constructor(
        public api: ApiService,
        private notification: NotificationService,
        private httpErrorHandler: HttpErrorHandler,
        private http: HttpClient
    ) {
    }

    downloadExcel(params: Record<string, any>): void {
        this.notification.openInfo("Download started.", NOTIFICATION_DURATION_MS)
        const httpParams = new HttpParams({fromObject: params});
        this.http.get('/api/GetExcel', {
            params: httpParams,
            responseType: 'blob',
            observe: 'response'
        }).pipe(
            catchError(this.httpErrorHandler.parseErrorBlob)
        )
            .subscribe({
                next: (response: HttpResponse<Blob>) => {
                    const blob: Blob = response.body as Blob;
                    const contentDisposition: string = response.headers.get('content-disposition');
                    let filename = 'data.xlsx'
                    if (contentDisposition) {
                        const match = contentDisposition.match(/filename="?([^";]+)"?/);
                        filename = match && match[1] ? match[1] : 'data.xlsx';
                    }
                    saveAs(blob, filename);
                    this.notification.openSuccess("Download succeeded.")
                },
                error: (e: any) => {
                    this.notification.openError(`There was an error downloading this data. ${e.message}`)
                }

            });
    }

    downloadPrintout(url: string, format: string): void {
        this.http.get(url, {observe: 'response', responseType: 'blob'}).subscribe((response: HttpResponse<Blob>) => {
            const blob = response.body as Blob;
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `download.${format}` // fallback
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^";]+)"?/);
                filename = match?.[1] || `download.${format}`;
            }
            saveAs(blob, filename);
        });
    }

    downloadFile(model_name: string, object_id: string, field: string, fileField: FileField): void {
        let filename: string = fileField.filename;
        this.http.get(`/api/download/${model_name}/${object_id}/${field}`, {
            responseType: 'blob',
            observe: 'response'
        }).pipe(
            tap((response: HttpResponse<Blob>): void => {
                const blob: Blob = response.body;
                if (fileField.mimetype === 'application/pdf') {
                    const contentDisposition: string | null = response.headers.get('Content-Disposition')
                    if (contentDisposition) {
                        const match = contentDisposition.match(/filename="?([^";]+)"?/);
                        filename = match?.[1] || fileField.filename;
                    }
                }
                saveAs(blob, filename)
            })
        ).subscribe()
    }

    downloadComponentDataCsv(start, end, component_type_id, constantPropertiesIds: string[] = []) {
        this.notification.openInfo("Download started.", NOTIFICATION_DURATION_MS)
        const requestParams: ComponentDataCsvRequestParams = {start, end, component_type_id, format: 'csv'};

        if (constantPropertiesIds.length) {
            requestParams.constant_properties_ids = constantPropertiesIds;
        }

        return this.api.get('/api/DownloadComponentData/', {
                responseType: 'blob',
                params: utils.httpParamSerializer(requestParams),
                observe: 'response',
            }
        ).pipe(catchError(this.httpErrorHandler.parseErrorBlob)).subscribe(response => {
            const contentDispositionHeader = response.headers.get('content-disposition');
            let fileName: string;
            if (contentDispositionHeader) {
                const parts: string[] = contentDispositionHeader.split(';');
                fileName = parts.length > 1 ? parts[1].split('=')[1] : 'componentData.csv';
            } else {
                fileName = 'componentData.csv';
            }
            const blob = new Blob([response.body], {type: 'application/csv'});
            const href = window.URL.createObjectURL(blob);
            this.downloadCsv(href, fileName);
        }, error => {
            if (error.status === 500) {
                this.notification.openError("Server error");
            } else {
                this.notification.openError('An error occurred. ' + error.message);
            }
            console.log('error', error);
        });
    }

    downloadModelData(model_name: string, filename: string, filters: {
                          [key: string]: any
                      }, columns: EventConfigColumn[],
                      cp_dict: IDMap<ConstantProperty>, ct_dict: IDMap<ComponentType>,
                      event_types: string[] = [], filetype: string = DEFAULT_FILE_DOWNLOAD_OPTION,
                      constantDateFilter?: ConstantDateFilter) {
        this.notification.openInfo("Download started.", NOTIFICATION_DURATION_MS)
        // TODO: check how and when the models are pulled down
        let cp_map: { [key: string]: { [key: string]: string | number } } = {};
        let ct_map: { [key: string]: { name: string; aggregation_type: string } } = {};
        let columns_renamed: string[] = [];
        let column_map: {
            [key: string]: string | { [key: string]: string | { name: string; aggregation_type: string } } | {
                [key: string]: { [key: string]: string | number }
            }
        } = {};

        columns.forEach((col) => {
            let title;
            const colId = getColumnId(col);

            if (col.type === 'attribute') {
                // Hybrid Property Columns (i.e. not CPs or Component Types)
                title = col.title || col.id;

                // Check if this is a relationship attribute (has related_type_id)
                if (col.related_type_id && ct_dict[col.related_type_id]) {
                    // Treat like a constant property - add to cp_map with compound ID
                    // The backend will detect it's an attribute and handle it correctly (utils_api.py lines 1049-1056)
                    const relatedTypeId = col.related_type_id;
                    const compoundId = `${relatedTypeId}:${col.id}`;

                    cp_map[title] = {
                        'name': compoundId,
                        'related_type': col.related_type || 'component_type',
                        'related_type_id': relatedTypeId,
                        'related_type_name': ct_dict[relatedTypeId]?.attributes?.name,
                        'property_name': col.id,
                        'aggregation_type': col.format?.aggregation_type || 'none'
                    };

                    // Ensure CT is in ct_map
                    const ctName = ct_dict[relatedTypeId]?.attributes?.name;
                    const alreadyExists = Object.values(ct_map).some(ct =>
                        typeof ct === 'object' && ct.name === ctName
                    );
                    if (ctName && !alreadyExists) {
                        ct_map[ctName] = {
                            name: ctName,
                            aggregation_type: col.format?.aggregation_type || 'default'
                        };
                    }
                } else {
                    // Regular attribute (no relationship) - just map the name
                    column_map[col.id] = title;
                }
            } else if (col.type === 'component_type') {
                // component type column
                title = col.title || ct_dict[col.id]?.attributes?.name || col.id;
                // Store as object with name and aggregation_type if specified
                ct_map[title] = {
                    name: ct_dict[col.id]?.attributes?.name,
                    aggregation_type: col.format?.aggregation_type || 'default'
                };
            } else if (col.type === 'constant_property') {
                // Check if this is a compound ID (relationship column)
                const isCompoundId = colId.includes(':');

                if (isCompoundId) {
                    // This is a relationship column with format "typeId:propertyId"
                    const [relatedTypeId, propertyId] = colId.split(':');
                    title = col.title || cp_dict[propertyId]?.attributes?.name || colId;
                    // Send the compound ID as the name to maintain relationship context
                    cp_map[title] = {'name': colId};  // Send full compound ID
                    // Set the relationship information using the compound ID parts
                    cp_map[title]['related_type'] = col.related_type || 'component_type';
                    cp_map[title]['related_type_id'] = relatedTypeId;
                    cp_map[title]['related_type_name'] = ct_dict[relatedTypeId]?.attributes?.name;
                    // Also send the actual property name for lookup
                    cp_map[title]['property_name'] = cp_dict[propertyId]?.attributes?.name || propertyId;
                    // Ensure related CT is in ct_map for backend resolution
                    if (ct_dict[relatedTypeId]) {
                        const ctName = ct_dict[relatedTypeId]?.attributes?.name;
                        // Check if any existing entry already maps to this CT (by name, not key)
                        const alreadyExists = Object.values(ct_map).some(ct =>
                            typeof ct === 'object' && ct.name === ctName
                        );
                        if (ctName && !alreadyExists) {
                            ct_map[ctName] = {
                                name: ctName,
                                aggregation_type: col.format?.aggregation_type || 'default'
                            };
                        }
                    }
                } else {
                    // Regular constant property (non-relationship)
                    title = col.title || cp_dict[col.id]?.attributes?.name || col.id;
                    cp_map[title] = {'name': cp_dict[col.id]?.attributes?.name};
                    // Check if it has relationship info specified separately
                    if (col.related_type_id) {
                        cp_map[title]['related_type'] = col.related_type;
                        cp_map[title]['related_type_id'] = col.related_type_id;
                        cp_map[title]['related_type_name'] = ct_dict[col.related_type_id]?.attributes.name;
                        // Ensure related CT is in ct_map for backend resolution
                        if (ct_dict[col.related_type_id]) {
                            const ctName = ct_dict[col.related_type_id]?.attributes?.name;
                            // Check if any existing entry already maps to this CT (by name, not key)
                            const alreadyExists = Object.values(ct_map).some(ct =>
                                typeof ct === 'object' && ct.name === ctName
                            );
                            if (ctName && !alreadyExists) {
                                ct_map[ctName] = {
                                    name: ctName,
                                    aggregation_type: col.format?.aggregation_type || 'default'
                                };
                            }
                        }
                    }
                }

                // Add decimal formatting if specified
                if (col.format?.decimals || col.format?.decimals === 0) {
                    cp_map[title]['decimals'] = col.format.decimals;
                }
                // Add aggregation type if specified (for relationship columns)
                if (col.format?.aggregation_type) {
                    cp_map[title]['aggregation_type'] = col.format.aggregation_type;
                }
            }
            columns_renamed.push(title);
        });
        column_map['constant_properties'] = cp_map;
        column_map['component_types'] = ct_map;
        let get_url = '/api/utils/download_model?filename=' + encodeURIComponent(filename) + '&filters=' +
            encodeURIComponent(JSON.stringify(filters)) + '&columns=' +
            encodeURIComponent(JSON.stringify(columns_renamed)) + '&column_map=' +
            encodeURIComponent(JSON.stringify(column_map)) + '&model_name=' +
            model_name + '&filetype=' + filetype;

        if (!!event_types.length) {
            get_url += '&event_types=' + encodeURIComponent(JSON.stringify(event_types));
        }
        if (constantDateFilter) {
            get_url += '&constant_date_filter=' + encodeURIComponent(JSON.stringify(constantDateFilter));
        }
        const headers = new HttpHeaders({
            'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
            'Pragma': 'no-cache',
            'Expires': '0'
        });

        this.http.get(get_url, {headers: headers, responseType: 'blob'})
            .pipe(
                catchError(this.httpErrorHandler.parseErrorBlob)
            )
            .subscribe({
                next: (response: any) => {
                    if (!response || response.status_code === 204) {
                        this.notification.openError('No Data Found for the selected time range and filters.');
                    } else {
                        const link = document.createElement("a");
                        link.href = URL.createObjectURL(response);
                        link.download = filename + "." + (filetype.includes('_csv') ? 'csv' : 'xlsx');
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        this.notification.openSuccess("Download succeeded.")
                    }
                },
                error: (errors) => {
                    const message = errors?.message || 'Unknown download error';
                    this.notification.openError(`Failed to generate file. ${message}`);
                    console.log('reason for fail', errors);
                }
            })
    }

    downloadCsv(href, title: string, extension: '.csv' | '.xlsx' = '.csv') {
        const a = document.createElement("a");
        a.href = href;
        a.download = title + extension;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        this.notification.openSuccess("Download succeeded.")
    }
}
